import React, { Component } from 'react';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { useOrderDetail } from '../../lib/order-detail-hooks';
import { MapPin, Phone, Clock, Map, Navigation, Receipt, CreditCard, UtensilsCrossed, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
interface OrderDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string | null;
  onStatusChange?: () => void;
}
const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);
  if (diffInMinutes < 1) return 'Hace un momento';
  if (diffInMinutes < 60) return `Hace ${diffInMinutes} min`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `Hace ${diffInHours} h`;
  return date.toLocaleDateString();
};
export function OrderDetailDrawer({
  isOpen,
  onClose,
  orderId,
  onStatusChange
}: OrderDetailDrawerProps) {
  const {
    order,
    loading,
    error
  } = useOrderDetail(orderId);
  if (!isOpen) return null;
  const handleStatusChange = async (newStatus: string) => {
    if (!order) return;
    try {
      const {
        error
      } = await supabase.from('orders').update({
        status: newStatus
      }).eq('id', order.id);
      if (error) throw error;
      if (onStatusChange) onStatusChange();
      onClose();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Error al actualizar el estado');
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'accepted':
        return 'info';
      case 'preparing':
        return 'info';
      case 'ready':
        return 'success';
      case 'delivered':
        return 'default';
      case 'cancelled':
        return 'danger';
      default:
        return 'default';
    }
  };
  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'accepted':
        return 'Aceptado';
      case 'preparing':
        return 'Preparando';
      case 'ready':
        return 'Listo';
      case 'delivered':
        return 'Entregado';
      case 'cancelled':
        return 'Cancelado';
      default:
        return status;
    }
  };
  const renderOptions = (options: any[]) => {
    if (!options || options.length === 0) return null;
    const grouped = options.reduce((acc, opt) => {
      const group = opt.option_group || 'Otros';
      if (!acc[group]) acc[group] = [];
      acc[group].push(opt);
      return acc;
    }, {} as Record<string, any[]>);
    const groupOrder = ['Base', 'Proteína', 'Toppings', 'Premium', 'Aderezos', 'Semillas'];
    const sortedGroups = Object.keys(grouped).sort((a, b) => {
      const indexA = groupOrder.indexOf(a);
      const indexB = groupOrder.indexOf(b);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
    return <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 pl-4 border-l-2 border-gray-200 dark:border-gray-700" data-id="element-693">
        {sortedGroups.map(group => <div key={group} className="mb-1" data-id="element-694">
            <span className="font-semibold text-gray-700 dark:text-gray-300" data-id="element-695">
              {group}:{' '}
            </span>
            {grouped[group].map((opt, idx) => <span key={opt.id} data-id="element-696">
                {opt.option_name_snapshot}
                {opt.option_price_snapshot > 0 && ` (+${formatCurrency(opt.option_price_snapshot)})`}
                {idx < grouped[group].length - 1 ? ', ' : ''}
              </span>)}
          </div>)}
      </div>;
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={order ? `Pedido #${order.order_number}` : 'Detalle del Pedido'} data-id="element-697">
      {loading ? <div className="p-8 text-center text-gray-500 dark:text-gray-400" data-id="element-698">
          Cargando detalles...
        </div> : error ? <div className="p-8 text-center text-red-500" data-id="element-699">
          Error al cargar el pedido
        </div> : order ? <div className="space-y-6" data-id="element-700">
          {/* 1. HEADER */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg" data-id="element-701">
            <div data-id="element-702">
              <div className="flex items-center gap-3 mb-2" data-id="element-703">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-704">
                  #{order.order_number}
                </h2>
                <Badge variant={getStatusColor(order.status) as any} data-id="element-705">
                  {getStatusText(order.status)}
                </Badge>
                <Badge variant={order.delivery_type === 'delivery' ? 'info' : 'default'} data-id="element-706">
                  {order.delivery_type === 'delivery' ? 'Delivery' : order.delivery_type === 'pickup' ? 'Para Llevar' : 'En Mesa'}
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-707">
                <Clock className="w-4 h-4" data-id="element-708" />
                <span data-id="element-709">{formatTimeAgo(order.created_at)}</span>
              </div>
            </div>
            <div className="text-left md:text-right" data-id="element-710">
              <div className="font-medium text-gray-900 dark:text-white" data-id="element-711">
                {order.customer_name || 'Cliente sin nombre'}
              </div>
              {order.customer_phone && <a href={`tel:${order.customer_phone}`} className="flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline mt-1 justify-start md:justify-end" data-id="element-712">
                  <Phone className="w-4 h-4" data-id="element-713" />
                  {order.customer_phone}
                </a>}
            </div>
          </div>

          {/* 2. DELIVERY SECTION */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-id="element-714">
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-715">
              <MapPin className="w-4 h-4" data-id="element-716" />
              Detalles de Entrega
            </h3>
            {order.delivery_type === 'delivery' && <div className="space-y-3" data-id="element-717">
                <p className="text-gray-900 dark:text-white" data-id="element-718">
                  {order.delivery_address || 'Sin dirección especificada'}
                </p>
                {order.maps_url && <div className="flex gap-3" data-id="element-719">
                    <a href={order.maps_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline" data-id="element-720">
                      <Map className="w-4 h-4" data-id="element-721" /> Google Maps
                    </a>
                    <a href={`https://waze.com/ul?q=${encodeURIComponent(order.delivery_address || '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline" data-id="element-722">
                      <Navigation className="w-4 h-4" data-id="element-723" /> Waze
                    </a>
                  </div>}
              </div>}
            {order.delivery_type === 'dine_in' && <p className="text-gray-900 dark:text-white font-medium" data-id="element-724">
                Mesa: {order.table_number || 'No especificada'}
              </p>}
            {order.delivery_type === 'pickup' && <p className="text-gray-900 dark:text-white font-medium" data-id="element-725">
                Pasará a recoger
              </p>}
          </div>

          {/* 3. PAYMENT SECTION */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-id="element-726">
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-727">
              <CreditCard className="w-4 h-4" data-id="element-728" />
              Pago
            </h3>
            <div className="flex flex-col gap-1" data-id="element-729">
              <p className="text-gray-900 dark:text-white capitalize" data-id="element-730">
                Método:{' '}
                <span className="font-medium" data-id="element-731">
                  {order.payment_method === 'cash' ? 'Efectivo' : order.payment_method === 'card' ? 'Tarjeta' : order.payment_method === 'transfer' ? 'Transferencia' : order.payment_method}
                </span>
              </p>
              {order.payment_method === 'cash' && order.cash_amount && <>
                  <p className="text-gray-600 dark:text-gray-400" data-id="element-732">
                    Paga con: {formatCurrency(order.cash_amount)}
                  </p>
                  <p className="text-gray-600 dark:text-gray-400" data-id="element-733">
                    Cambio:{' '}
                    {formatCurrency(Math.max(0, order.cash_amount - order.total))}
                  </p>
                </>}
            </div>
          </div>

          {/* 4. ORDER ITEMS SECTION */}
          <div data-id="element-734">
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-735">
              <UtensilsCrossed className="w-4 h-4" data-id="element-736" />
              Artículos del Pedido
            </h3>
            {(!order.items || order.items.length === 0) && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-sm" data-id="element-737">
                <p className="font-semibold text-red-800 dark:text-red-300 mb-1" data-id="element-738">
                  ⚠️ No se pudieron cargar los artículos de este pedido
                </p>
                <p className="text-red-700 dark:text-red-400" data-id="element-739">
                  Order ID: <code data-id="element-740">{order.id}</code> — total ₡{order.total}. Esto
                  suele ser un problema de permisos (RLS) o que los artículos no
                  se guardaron al crear la orden. Revisa la consola del
                  navegador y la tabla <code data-id="element-741">order_items</code> en Supabase.
                </p>
              </div>}
            <div className="space-y-3" data-id="element-742">
              {order.items.map((item: any) => <div key={item.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-id="element-743">
                  <div className="flex justify-between items-start mb-2" data-id="element-744">
                    <div data-id="element-745">
                      <div className="flex items-center gap-2" data-id="element-746">
                        <span className="font-bold text-gray-900 dark:text-white" data-id="element-747">
                          {item.quantity}x
                        </span>
                        <span className="font-semibold text-gray-900 dark:text-white" data-id="element-748">
                          {item.item_name_snapshot}
                        </span>
                      </div>
                      {item.station_code && <Badge variant="default" className="mt-1 text-xs" data-id="element-749">
                          {item.station_code}
                        </Badge>}
                    </div>
                    <span className="font-medium text-gray-900 dark:text-white" data-id="element-750">
                      {formatCurrency(item.line_total)}
                    </span>
                  </div>

                  {renderOptions(item.options)}

                  {item.notes && <div className="mt-3 text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded flex items-start gap-2" data-id="element-751">
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-752" />
                      <p data-id="element-753">Nota: {item.notes}</p>
                    </div>}
                </div>)}
            </div>
          </div>

          {/* 5. GENERAL NOTES SECTION */}
          {(order.notes || order.allergies) && <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded-lg p-4" data-id="element-754">
              <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-500 uppercase tracking-wider mb-2" data-id="element-755">
                Notas Generales y Alergias
              </h3>
              {order.allergies && <p className="text-amber-900 dark:text-amber-400 font-medium mb-1" data-id="element-756">
                  Alergias: {order.allergies}
                </p>}
              {order.notes && <p className="text-amber-800 dark:text-amber-300" data-id="element-757">
                  {order.notes}
                </p>}
            </div>}

          {/* 6. TOTALS SECTION */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-2" data-id="element-758">
            <div className="flex justify-between text-gray-600 dark:text-gray-400" data-id="element-759">
              <span data-id="element-760">Subtotal</span>
              <span data-id="element-761">{formatCurrency(order.subtotal)}</span>
            </div>
            {order.delivery_fee > 0 && <div className="flex justify-between text-gray-600 dark:text-gray-400" data-id="element-762">
                <span data-id="element-763">Costo de envío</span>
                <span data-id="element-764">{formatCurrency(order.delivery_fee)}</span>
              </div>}
            <div className="flex justify-between text-xl font-bold text-gray-900 dark:text-white pt-2 border-t border-gray-200 dark:border-gray-700" data-id="element-765">
              <span data-id="element-766">Total</span>
              <span data-id="element-767">{formatCurrency(order.total)}</span>
            </div>
          </div>

          {/* 7. LINKS SECTION */}
          <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-768">
            {order.tracking_token && <Button variant="secondary" size="sm" onClick={() => window.open(`/tracking/${order.tracking_token}`, '_blank')} data-id="element-769">
                Ver Tracking
              </Button>}
            {order.ticket_image_url && <Button variant="secondary" size="sm" onClick={() => window.open(order.ticket_image_url, '_blank')} data-id="element-770">
                <Receipt className="w-4 h-4 mr-2" data-id="element-771" />
                Ver Ticket
              </Button>}
          </div>

          {/* ACTIONS */}
          <div className="flex flex-wrap gap-2 pt-6 mt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-772">
            {order.status === 'pending' && <Button variant="primary" onClick={() => handleStatusChange('accepted')} data-id="element-773">
                Aceptar Pedido
              </Button>}
            {(order.status === 'pending' || order.status === 'accepted') && <Button variant="secondary" onClick={() => handleStatusChange('preparing')} data-id="element-774">
                Marcar Preparando
              </Button>}
            {order.status === 'preparing' && <Button variant="primary" onClick={() => handleStatusChange('ready')} data-id="element-775">
                Marcar Listo
              </Button>}
            {order.status === 'ready' && <Button variant="primary" onClick={() => handleStatusChange('delivered')} data-id="element-776">
                Marcar Entregado
              </Button>}
            {order.status !== 'cancelled' && order.status !== 'delivered' && <Button variant="danger" onClick={() => {
          if (window.confirm('¿Estás seguro de cancelar este pedido?')) {
            handleStatusChange('cancelled');
          }
        }} data-id="element-777">
                Cancelar
              </Button>}
          </div>
        </div> : null}
    </Modal>;
}